import { boundedCache, cached } from './agents-bird.cache';
import { type ToneField } from './agents-bird.tone';

export type HalftoneGrid = {
  x: Float32Array;
  y: Float32Array;
  pitch: number;
};

const SCREEN_ANGLE = Math.PI / 4;
const DOT_GAIN = 0.72;
const MIN_RADIUS = 0.35;
const COMPACT_SIZE = 52;
const MEDIUM_SIZE = 84;
const LARGE_SIZE = 150;
const GRID_CACHE_LIMIT = 32;

const grids = boundedCache<HalftoneGrid>(GRID_CACHE_LIMIT);

export const isCompact = (cssSize: number) => cssSize <= COMPACT_SIZE;

export const cellsAcross = (cssSize: number) =>
  cssSize <= MEDIUM_SIZE ? 34 : cssSize <= LARGE_SIZE ? 48 : 84;

export const latticeGrid = (pixels: number, step: number): HalftoneGrid =>
  cached(grids, `lattice:${pixels}:${step}`, () => {
    const xs: number[] = [];
    const ys: number[] = [];
    for (let row = 0; row * step < pixels + step; row++) {
      for (let x = (row % 2) * step; x < pixels + step; x += step * 2) {
        xs.push(x + 0.5);
        ys.push(row * step + 0.5);
      }
    }
    return {
      x: Float32Array.from(xs),
      y: Float32Array.from(ys),
      pitch: step * Math.SQRT2,
    };
  });

export const halftoneGrid = (pixels: number, cells: number): HalftoneGrid =>
  cached(grids, `${pixels}:${cells}`, () => {
    const pitch = pixels / cells;
    const cos = Math.cos(SCREEN_ANGLE);
    const sin = Math.sin(SCREEN_ANGLE);
    const reach = Math.ceil((pixels * 0.75) / pitch) + 1;
    const centre = pixels / 2;
    const xs: number[] = [];
    const ys: number[] = [];
    for (let a = -reach; a <= reach; a++) {
      for (let b = -reach; b <= reach; b++) {
        const x = centre + (a * cos - b * sin) * pitch;
        const y = centre + (a * sin + b * cos) * pitch;
        const inside =
          x > -pitch && y > -pitch && x < pixels + pitch && y < pixels + pitch;
        if (!inside) continue;
        xs.push(x);
        ys.push(y);
      }
    }
    return { x: Float32Array.from(xs), y: Float32Array.from(ys), pitch };
  });

const sample = (values: Float32Array, size: number, u: number, v: number) => {
  const x = Math.min(size - 2, Math.max(0, u));
  const y = Math.min(size - 2, Math.max(0, v));
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const i = iy * size + ix;
  return (
    values[i] * (1 - fx) * (1 - fy) +
    values[i + 1] * fx * (1 - fy) +
    values[i + size] * (1 - fx) * fy +
    values[i + size + 1] * fx * fy
  );
};

export type HalftoneTone = { floor: number; clipped: boolean };

export const halftoneRadii = (
  grid: HalftoneGrid,
  field: ToneField,
  pixels: number,
  dark: boolean,
  { floor, clipped }: HalftoneTone
) => {
  const shades = dark ? field.brightness : field.darkness;
  const scale = field.size / pixels;
  const radii = new Float32Array(grid.x.length);
  for (let i = 0; i < radii.length; i++) {
    const u = grid.x[i] * scale;
    const v = grid.y[i] * scale;
    const coverage = sample(field.coverage, field.size, u, v);
    const shade = sample(shades, field.size, u, v);
    const tone = Math.max(shade, floor);
    const value = clipped ? (coverage > 0 ? tone : 0) : coverage * tone;
    const radius = grid.pitch * DOT_GAIN * Math.sqrt(value);
    radii[i] = radius < MIN_RADIUS ? 0 : radius;
  }
  return radii;
};

export type HalftoneStyle = { color: string; alpha: number };

export const drawHalftone = (
  context: CanvasRenderingContext2D,
  grid: HalftoneGrid,
  radii: Float32Array,
  { color, alpha }: HalftoneStyle
) => {
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  context.fillStyle = color;
  context.globalAlpha = alpha;
  context.beginPath();
  for (let i = 0; i < radii.length; i++) {
    if (radii[i] === 0) continue;
    context.moveTo(grid.x[i] + radii[i], grid.y[i]);
    context.arc(grid.x[i], grid.y[i], radii[i], 0, Math.PI * 2);
  }
  context.fill();
  context.globalAlpha = 1;
};

export const waveRadii = (
  base: Float32Array,
  grid: HalftoneGrid,
  origin: { x: number; y: number },
  front: number,
  strength: number,
  into: Float32Array
) => {
  const thickness = grid.pitch * 2.2;
  for (let i = 0; i < into.length; i++) {
    const distance = Math.hypot(grid.x[i] - origin.x, grid.y[i] - origin.y);
    const wave = Math.exp(-(((distance - front) / thickness) ** 2));
    into[i] = base[i] * (1 + strength * wave);
  }
  return into;
};

export const blendRadii = (
  from: Float32Array,
  to: Float32Array,
  amount: number,
  into: Float32Array
) => {
  for (let i = 0; i < into.length; i++)
    into[i] = from[i] + (to[i] - from[i]) * amount;
  return into;
};
